// src/widgets/WidgetGallery.tsx
import { useState } from 'react';
import {
	Modal,
	Text,
	SimpleGrid,
	Paper,
	Group,
	Stack,
	ThemeIcon,
	Box,
	useMantineColorScheme,
} from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { widgetRegistry } from './registry';

interface WidgetGalleryProps {
	opened: boolean;
	onClose: () => void;
	enabledWidgets: string[];
	onToggleWidget: (widgetId: string) => void;
}

function WidgetGallery({
	opened,
	onClose,
	enabledWidgets,
	onToggleWidget,
}: WidgetGalleryProps) {
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === 'dark';

	// Track which card is being pressed for animation
	const [pressedId, setPressedId] = useState<string | null>(null);

	const handleToggle = (widgetId: string) => {
		setPressedId(widgetId);
		onToggleWidget(widgetId);
		// Reset pressed state after animation
		setTimeout(() => setPressedId(null), 150);
	};

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={
				<Text fw={700} size='lg'>
					Widget Gallery
				</Text>
			}
			size='lg'
			centered
			styles={{
				header: {
					paddingBottom: 0,
				},
			}}
		>
			<Text size='sm' c='dimmed' mb='lg'>
				Tap a widget to add or remove it from your dashboard.
			</Text>

			<SimpleGrid cols={{ base: 1, sm: 2 }} spacing='md'>
				{widgetRegistry.map((widget) => {
					const isEnabled = enabledWidgets.includes(widget.id);
					const isPressed = pressedId === widget.id;

					return (
						<Paper
							key={widget.id}
							p='md'
							radius='md'
							onClick={() => handleToggle(widget.id)}
							style={{
								cursor: 'pointer',
								position: 'relative',
								overflow: 'hidden',
								transform: isPressed ? 'scale(0.97)' : 'scale(1)',
								transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
								border: isEnabled
									? `2px solid var(--mantine-color-green-${isDark ? '7' : '5'})`
									: `1px solid var(--mantine-color-${isDark ? 'dark-4' : 'gray-3'})`,
								background: isEnabled
									? isDark
										? 'linear-gradient(135deg, rgba(64, 192, 87, 0.08) 0%, rgba(64, 192, 87, 0.02) 100%)'
										: 'linear-gradient(135deg, rgba(64, 192, 87, 0.08) 0%, rgba(255, 255, 255, 1) 100%)'
									: isDark
										? 'var(--mantine-color-dark-6)'
										: 'white',
								boxShadow: isEnabled
									? isDark
										? '0 0 20px rgba(64, 192, 87, 0.15)'
										: '0 0 20px rgba(64, 192, 87, 0.12)'
									: 'none',
							}}
						>
							{/* Selection indicator - top right corner */}
							<Box
								style={{
									position: 'absolute',
									top: 12,
									right: 12,
									width: 24,
									height: 24,
									borderRadius: '50%',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
									background: isEnabled
										? 'var(--mantine-color-green-6)'
										: isDark
											? 'var(--mantine-color-dark-4)'
											: 'var(--mantine-color-gray-2)',
									transform: isEnabled ? 'scale(1)' : 'scale(0.85)',
								}}
							>
								<IconCheck
									size={14}
									style={{
										color: isEnabled
											? 'white'
											: isDark
												? 'var(--mantine-color-dark-2)'
												: 'var(--mantine-color-gray-5)',
										transition: 'all 200ms ease',
										opacity: isEnabled ? 1 : 0.5,
									}}
									strokeWidth={3}
								/>
							</Box>

							<Stack gap='sm'>
								{/* Icon and title */}
								<Group gap='sm' align='center'>
									<ThemeIcon
										size={44}
										radius='md'
										variant={isEnabled ? 'light' : 'light'}
										color={isEnabled ? 'green' : isDark ? 'gray' : 'blue'}
										style={{
											transition: 'all 200ms ease',
										}}
									>
										<widget.icon
											size={24}
											style={{
												opacity: isEnabled ? 1 : 0.7,
												transition: 'opacity 200ms ease',
											}}
										/>
									</ThemeIcon>
									<Box style={{ flex: 1, paddingRight: 32 }}>
										<Text
											fw={600}
											size='md'
											style={{
												transition: 'color 200ms ease',
												color: isEnabled
													? isDark
														? 'var(--mantine-color-green-4)'
														: 'var(--mantine-color-green-7)'
													: undefined,
											}}
										>
											{widget.name}
										</Text>
									</Box>
								</Group>

								{/* Description */}
								<Text
									size='sm'
									c='dimmed'
									lh={1.5}
									style={{
										opacity: isEnabled ? 1 : 0.8,
										transition: 'opacity 200ms ease',
									}}
								>
									{widget.description}
								</Text>

								{/* Status indicator */}
								<Text
									size='xs'
									fw={500}
									style={{
										color: isEnabled
											? 'var(--mantine-color-green-6)'
											: isDark
												? 'var(--mantine-color-dark-2)'
												: 'var(--mantine-color-gray-5)',
										transition: 'color 200ms ease',
									}}
								>
									{isEnabled ? 'Added to dashboard' : 'Tap to add'}
								</Text>
							</Stack>
						</Paper>
					);
				})}
			</SimpleGrid>

			<Text size='xs' c='dimmed' ta='center' mt='xl'>
				More widgets coming soon!
			</Text>
		</Modal>
	);
}

export default WidgetGallery;
